// login component
import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import { storeUser } from "../../api-services/process";
import ImageCrop from "../../app-components/image-cropper/ImageCrop";
import Preloader from "../../app-components/preloader/Preloader";
import AuthLayout from "../../app-layouts/AuthLayout";
import { ELITELINKS, SITELINKS } from "../../app-routes/links";
import { successAlert } from "../../app-util/SweetAlert";
import { CameraIcon } from "../../assets/custom-icons";
import { HoneConnectLogo } from "../../assets/img";
import { CiPower } from "react-icons/ci";

function isLocation(value: any): value is Location {
  return (
    value &&
    typeof value.latitude === "number" &&
    typeof value.longitude === "number"
  );
}

const RegisterStepThree = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [coordinates, setCoordinates] = useState<any>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    handleGeoLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIconClick = () => {
    fileInputRef.current?.click();
  };

  const isValidImage = (file: File) => {
    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    return validTypes.includes(file.type);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (file && isValidImage(file)) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target?.result as string);
      reader.readAsDataURL(file);

      await uploadFile(file);
    } else {
      alert("Please select a valid PNG, JPG, or JPEG image.");
      setPreviewImage(null);
    }
  };

  const compressImage = (file: File, quality = 0.7): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Set canvas size to desired dimensions (optional resizing)
          const MAX_WIDTH = 800; // Resize width if image is too large
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;

          // Draw the image on canvas
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Compress image to Blob (JPEG with reduced quality)
          canvas.toBlob(
            (blob) => resolve(blob),
            "image/jpeg", // or "image/webp" for web-friendly compression
            quality, // Compression quality: 0.1 (low) to 1.0 (high)
          );
        };
      };
    });
  };

  const uploadFile = async (file: File) => {
    try {
      const compressedBlob = await compressImage(file, 0.7); // Adjust quality as needed
      if (!compressedBlob) throw new Error("Failed to compress image");

      const formData = new FormData();
      formData.append("profile_pix", compressedBlob, file.name);

      setPageLoading(true);
      await apiClient.post("upload-profile-pix", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent?.total || 1,
          );

          setUploadProgress(percentCompleted);
        },
      });
    } catch (error) {
      alert(error); // Display error message from apiClient
      setUploadProgress(0); // Reset progress on failure
      setPageLoading(false);
    } finally {
      setPageLoading(false);
    }
  };

  // handle-login-submit
  const handleSignUpStepThree = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");

    if (uploadProgress < 100) {
      alert("Please upload your profile picture");
      return;
    }

    try {
      setLoading(true);
      const data = {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      };
      const res = await apiClient.post("register-step-three", data);
      const user = res.data?.data?.user;

      successAlert();
      storeUser(user);

      navigate(ELITELINKS.ELITE);
    } catch (error: any) {
      console.log(error);
      setServerError(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  // Upload or process cropped image
  const handleCroppedImage = async (file: File) => {
    setUploadProgress(0);
    await uploadFile(file);
  };

  const logout = async () => {
    try {
      setLoading(true);
      await apiClient.post("logout");
      localStorage.clear();
      navigate(SITELINKS.LOGIN);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // code to get user current location
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = "Geolocation is not supported by this device.";
        setErrorMsg(error);
        return reject(new Error(error));
      }

      if ("permissions" in navigator && navigator.permissions.query) {
        navigator.permissions.query({ name: "geolocation" }).then((status) => {
          if (status.state === "denied") {
            const error =
              "Location access is blocked. Please enable it in your phone browser settings.";
            setErrorMsg(error);
            return reject(new Error(error));
          }

          // Safe to proceed now
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve(position.coords);
            },
            (error) => {
              let message = "";
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  message = "User denied the request for Geolocation.";
                  break;
                case error.POSITION_UNAVAILABLE:
                  message = "Location information is unavailable.";
                  break;
                case error.TIMEOUT:
                  message = "The request to get user location timed out.";
                  break;
                default:
                  message = "An unknown error occurred.";
              }

              setErrorMsg(`Error getting location: ${message}`);
              reject(new Error(message));
            },
            {
              enableHighAccuracy: true,
              timeout: 120000,
              maximumAge: 0,
            },
          );
        });
      } else {
        // Permissions API not available, just try geolocation
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position.coords);
          },
          (error) => {
            let message = "";
            switch (error.code) {
              case error.PERMISSION_DENIED:
                message = "User denied the request for Geolocation.";
                break;
              case error.POSITION_UNAVAILABLE:
                message = "Location information is unavailable.";
                break;
              case error.TIMEOUT:
                message = "The request to get user location timed out.";
                break;
              default:
                message = "An unknown error occurred.";
            }
            setErrorMsg(
              `Error getting location: ${message} ${JSON.stringify(error)}`,
            );
            reject(new Error(message));
          },
          {
            enableHighAccuracy: true,
            timeout: 120000,
            maximumAge: 0,
          },
        );
      }
    });
  };

  const withTimeout = (promise: Promise<any>, timeout = 120000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Geolocation timed out")), timeout),
      ),
    ]);

  const handleGeoLocation = async () => {
    // e.preventDefault();
    setLoading(true);

    let position;
    try {
      position = await withTimeout(getUserLocation());
    } catch (error) {
      setErrorMsg(
        error instanceof Error
          ? `Error getting your location: ${error.message}`
          : "Unknown error getting your location.",
      );
      setLoading(false);
      return;
    }

    if (isLocation(position)) {
      const coordinates: Location = position;
      setCoordinates(coordinates);
      setLoading(false);
    } else {
      setErrorMsg(
        `Invalid location data received: ${JSON.stringify(position)}`,
      );
    }
  };

  return (
    <AuthLayout>
      <div className="lg:col-span-3 bg-white shadow relative h-[100vh]">
        <div className="bg-white w-full mt-5 pl-5">
          <button
            type="button"
            onClick={() => {
              logout();
            }}
            className="fixed gap-4 p-2 hover:bg-gray-700 dark:hover:bg-slate-800 bg-red-700 text-white shadow rounded-full font-[600]"
          >
            <CiPower className="text-2xl" />
          </button>

          <div className="w-1/2 mx-auto pt-12 flex gap-2 mb-10 justify-center">
            <div className="bg-gray-300 h-[5px] w-[40px] shadow rounded-full"></div>
            <div className="bg-gray-300 h-[5px] w-[40px] shadow rounded-full"></div>
            <div className="bg-honeConnectPurple h-[5px] w-[40px] shadow rounded-full"></div>
          </div>

          <div className="text-sm font-semibold flex justify-center px-4">
            <Link to="#!" className="text-honeConnectPurple flex">
              <img
                src={HoneConnectLogo}
                alt="HoneConnect"
                className="h-[35px]"
              />
            </Link>

            <p className="uppercase text-honeConnectGray hidden sm:block">
              Step 2/3
            </p>
          </div>

          {/* <!-- page lead text --> */}
          <div className="px-5 pt-6 pb-5">
            <h4 className="text-[2.5rem] text-honeConnectBlack font-medium mb-2.5 text-honeConnectPurple leading-[35px] text-center">
              Upload profile image
            </h4>
          </div>
        </div>

        <div className="relative top-[-10px] w-full pt-4 lg:pt-20 pb-0 px-5 lg:px-10 lg:px-28 text-honeConnectGray bg-white rounded-t-lg">
          <form
            onSubmit={handleSignUpStepThree}
            autoComplete="off"
            autoCorrect="off"
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col text-center justify-center text-sm font-medium">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/png, image/jpg, image/jpeg" // Restrict file types
                onChange={handleFileChange}
              />

              <div className="flex justify-center mt-2 hidden">
                <img
                  src={previewImage || CameraIcon} // Show preview if available, otherwise CameraIcon
                  className="w-2/3 p-1 rounded-lg text-center border-2 border-dashed border-honeConnectOrange cursor-pointer"
                  onClick={handleIconClick}
                  alt="Upload Preview"
                />
              </div>

              <ImageCrop onImageSelect={handleCroppedImage} />

              {/* <p>Upload a picture</p> */}
              {uploadProgress > 0 && (
                <div className="flex justify-center items-center gap-1 mt-2">
                  <p>{uploadProgress}%</p>

                  <div
                    className={`h-[4px] flex w-full ${
                      uploadProgress === 100 ? "bg-green-700" : "bg-[#ffb7a0]"
                    } rounded-lg`}
                  >
                    <div
                      className={`h-[4px] ${
                        uploadProgress === 100
                          ? "bg-green-700"
                          : "bg-honeConnectOrange"
                      } rounded-lg`}
                      style={{ width: uploadProgress + "%" }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <p className="text-red-600 text-[12px] text-center mb-1">
              {serverError} {errorMsg}
            </p>

            <button
              type="submit"
              className="disabled:bg-gray-600 bg-honeConnectPurple text-white mb-10 text-sm w-full p-3 py-4 border-0 mt-2 hover:shadow-2xl rounded-lg"
              disabled={loading}
            >
              {loading ? "Loading..." : "Confirm"}
            </button>
          </form>
        </div>
      </div>

      <Preloader loading={pageLoading} />
    </AuthLayout>
  );
};

export default RegisterStepThree;
